import type { ExtractPreviewResponse } from '@/shared/hooks/api/userStandards'
import { useEntityDisplayLocales } from '../../hooks/useEntityDisplayLocales'
import {
  resolveLocalizedRiskReductionMeasureField,
  resolveRiskReductionMeasureAvailableLocales,
} from '@/shared/lib/standardEntityLocalizations'
import { StandardEntityLocaleToggle } from '../StandardEntityLocaleToggle'
import {
  ExtractionPreviewImpedimentBanner,
  ExtractionPreviewNoData,
  ExtractionPreviewSectionNotFound,
  type SectionExtractionPreviewContentProps,
} from './standardExtractionPreviewShared'

type StandardRiskReductionMeasuresExtractionPreviewContentProps =
  SectionExtractionPreviewContentProps & {
    preview: ExtractPreviewResponse
  }

export function StandardRiskReductionMeasuresExtractionPreviewContent({
  preview,
  contentVariant = 'display',
  uiState,
  resolveReason,
  translate,
}: StandardRiskReductionMeasuresExtractionPreviewContentProps) {
  const { getDisplayLocale, selectItemLocale } = useEntityDisplayLocales()
  const useSourceFields = contentVariant === 'source'
  const rrms = preview.riskReductionMeasures?.measures ?? []
  const rrmsImpediment = preview.riskReductionMeasuresImpediment ?? null

  if (uiState.sectionNotFound) {
    return (
      <ExtractionPreviewSectionNotFound
        reasonCode={rrmsImpediment?.reason ?? 'rrms_not_found'}
        resolveReason={resolveReason}
        translate={translate}
      />
    )
  }
  if (rrms.length > 0) {
    return (
      <ul className='space-y-0 divide-y rounded-md border px-4 py-2'>
        {rrms.map((rrm) => {
          const availableLocales =
            resolveRiskReductionMeasureAvailableLocales(rrm)
          const itemLocale = getDisplayLocale(rrm.id)
          return (
            <li
              key={rrm.id}
              className='space-y-1 py-3 first:pt-0 last:pb-0 first:last:py-0'
            >
              <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0 flex-1 space-y-1'>
                  <div className='font-medium'>
                    {rrm.clauseReference != null &&
                      rrm.clauseReference.trim() !== '' && (
                        <span className='text-muted-foreground'>
                          {rrm.clauseReference.trim()}.{' '}
                        </span>
                      )}
                    {(useSourceFields
                      ? rrm.name
                      : resolveLocalizedRiskReductionMeasureField(
                          rrm,
                          itemLocale,
                          'name'
                        )) || '—'}
                  </div>
                  <div className='text-muted-foreground'>{rrm.type ?? '—'}</div>
                  {rrm.type === 'SCP' && (
                    <div className='text-sm text-muted-foreground'>
                      {translate('detail.extractPreview.dependsOnControlSystemLabel')}{' '}
                      {rrm.dependsOnControlSystem
                        ? translate('detail.extractPreview.yes')
                        : translate('detail.extractPreview.no')}
                    </div>
                  )}
                  <div>
                    {(useSourceFields
                      ? rrm.description
                      : resolveLocalizedRiskReductionMeasureField(
                          rrm,
                          itemLocale,
                          'description'
                        )) || '—'}
                  </div>
                </div>
                {!useSourceFields && (
                  <StandardEntityLocaleToggle
                    availableLocales={availableLocales}
                    selectedLocale={itemLocale}
                    onSelectLocale={(locale) => selectItemLocale(rrm.id, locale)}
                  />
                )}
              </div>
            </li>
          )
        })}
      </ul>
    )
  }
  if (rrmsImpediment != null && rrmsImpediment.reason) {
    return (
      <ExtractionPreviewImpedimentBanner
        label={translate('detail.extractPreview.noRrmsExtracted')}
        reason={resolveReason(rrmsImpediment.reason)}
      />
    )
  }
  return <ExtractionPreviewNoData translate={translate} />
}
